import React from "react";
import { motion } from "framer-motion";

const EMAIL = "pulsesupport4@gmail.com";
const UPDATED = "June 2026";

const fade = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45 } },
};

const Section = ({ id, title, children }) => (
  <motion.section id={id} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={fade} className="scroll-mt-24">
    <h3 className="text-xl font-bold text-pulse-navy">{title}</h3>
    <div className="mt-3 space-y-3 leading-[1.8] text-charcoal/80">{children}</div>
  </motion.section>
);

export default function Legal() {
  return (
    <div className="bg-white" data-testid="legal-page">
      <section className="relative overflow-hidden bg-pulse-navy text-white grain-overlay">
        <div className="relative z-10 mx-auto max-w-3xl px-5 py-16 sm:px-8 sm:py-24">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-pulse-green">Legal</span>
          <h1 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl">Privacy Policy &amp; Terms</h1>
          <p className="mt-4 text-white/60">Last updated: {UPDATED}</p>
          <div className="mt-6 flex gap-3">
            <a href="#privacy" className="rounded-md bg-white/10 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-white/20">Privacy Policy</a>
            <a href="#terms" className="rounded-md bg-white/10 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-white/20">Terms of Service</a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-3xl px-5 py-14 sm:px-8 sm:py-20">
        <p className="rounded-lg bg-cloud/60 p-5 text-sm text-slate">
          Pulse is operated by <b className="text-pulse-navy">Jbros Innovation Pvt. Ltd.</b> ("Pulse", "we", "us"), based in Raipur, Chhattisgarh, India.
          This page explains, in plain language, what data we handle and the terms for using Pulse. Questions? Email us at{" "}
          <a href={`mailto:${EMAIL}`} className="font-medium text-pulse-navy underline">{EMAIL}</a>.
        </p>

        {/* ---------------- PRIVACY ---------------- */}
        <h2 id="privacy" className="mt-14 scroll-mt-24 text-2xl font-bold text-pulse-navy sm:text-3xl">Privacy Policy</h2>
        <div className="mt-8 space-y-10">
          <Section title="What we collect">
            <ul className="list-disc space-y-2 pl-5">
              <li><b>Account info from Google:</b> when you sign in with Google, we receive your name, email address and profile photo. That's it — we never see your Google password.</li>
              <li><b>Business details you give us:</b> your business name, venue names, WhatsApp number and email — the things needed to set up your calendar.</li>
              <li><b>Data you add in the app:</b> bookings, customer names for a booking, amounts, and any expenses you record.</li>
              <li><b>Basic technical info:</b> standard details like device/browser type and log data, used to keep the service secure and working.</li>
            </ul>
          </Section>

          <Section title="How we use it">
            <p>We use your information only to run Pulse for you: to show your calendar and bookings, calculate revenue and profit, send you account emails (welcome, employee invites, trial reminders), and provide support. We do <b>not</b> sell your data or use it for advertising.</p>
          </Section>

          <Section title="Signing in with Google">
            <p>We use Google Sign-In only to verify who you are. We request basic profile and email access — nothing more. We don't read your Gmail, contacts, files, or post anything on your behalf.</p>
          </Section>

          <Section title="Who we share it with">
            <p>We share data only with the trusted services that help Pulse run, and only as needed:</p>
            <ul className="list-disc space-y-2 pl-5">
              <li><b>Hosting &amp; database</b> — to securely store and serve your data.</li>
              <li><b>Email delivery (Resend)</b> — to send account and invite emails.</li>
              <li><b>WhatsApp</b> — only when you choose to chat with us via the WhatsApp link.</li>
            </ul>
            <p>We may also disclose information if required by law.</p>
          </Section>

          <Section title="Cookies">
            <p>We use a single secure session cookie to keep you signed in. We don't use third-party advertising or tracking cookies.</p>
          </Section>

          <Section title="How long we keep it & security">
            <p>We keep your data for as long as your account is active. We use reasonable technical and organisational measures to protect it. No online service can promise perfect security, but we take it seriously.</p>
          </Section>

          <Section title="Your choices">
            <p>You can ask us to access, correct, or delete your data at any time — just email <a href={`mailto:${EMAIL}`} className="font-medium text-pulse-navy underline">{EMAIL}</a> and we'll help. You can also stop using Pulse and request that your account be closed.</p>
          </Section>

          <Section title="Children">
            <p>Pulse is meant for businesses and is not intended for anyone under 18.</p>
          </Section>

          <Section title="Changes to this policy">
            <p>If we update this policy, we'll change the date at the top and, for significant changes, let you know in the app or by email.</p>
          </Section>
        </div>

        {/* ---------------- TERMS ---------------- */}
        <h2 id="terms" className="mt-16 scroll-mt-24 text-2xl font-bold text-pulse-navy sm:text-3xl">Terms of Service</h2>
        <div className="mt-8 space-y-10">
          <Section title="Agreeing to these terms">
            <p>By creating an account or using Pulse, you agree to these terms. If you're using Pulse on behalf of a business, you confirm you're allowed to accept these terms for it.</p>
          </Section>

          <Section title="What Pulse is">
            <p>Pulse is a simple tool to manage bookings, calendars, revenue and expenses for rental and venue businesses. Features may be added, changed or improved over time.</p>
          </Section>

          <Section title="Free trial">
            <p>New businesses get a <b>15-day free trial</b> with no card required. When the trial ends, access to the calendar may be paused until you choose to continue with us. We'll let you know before that happens.</p>
          </Section>

          <Section title="Your account & responsibilities">
            <ul className="list-disc space-y-2 pl-5">
              <li>Keep your login secure and don't share it. You're responsible for activity under your account.</li>
              <li>Provide accurate information, and use Pulse only for lawful purposes.</li>
              <li>The booking and business data you enter belongs to you. We simply store and display it for you.</li>
            </ul>
          </Section>

          <Section title="Acceptable use">
            <p>Please don't misuse Pulse — for example, don't try to break, overload, or reverse-engineer the service, upload harmful content, or use it to do anything illegal.</p>
          </Section>

          <Section title="Availability">
            <p>We work hard to keep Pulse running smoothly, but we can't guarantee it will always be available or error-free. We may occasionally need to perform maintenance or update features.</p>
          </Section>

          <Section title="Liability">
            <p>Pulse is provided "as is". To the extent allowed by law, we aren't liable for indirect or incidental losses, or for loss of data or profit arising from use of the service. Please keep your own records of important information.</p>
          </Section>

          <Section title="Ending your use">
            <p>You can stop using Pulse at any time. We may suspend or close an account that breaks these terms or misuses the service.</p>
          </Section>

          <Section title="Governing law">
            <p>These terms are governed by the laws of India, and any disputes will be handled by the courts in <b>Raipur, Chhattisgarh</b>.</p>
          </Section>

          <Section title="Contact">
            <p>For anything related to these terms or your privacy, reach us at <a href={`mailto:${EMAIL}`} className="font-medium text-pulse-navy underline">{EMAIL}</a>.</p>
          </Section>
        </div>
      </div>
    </div>
  );
}
